// Diagnostic suite for the attachment re-download pipeline.
//
// Five groups, mirroring iOS AttachmentRedownloadSelfTest:
//
//   A. Policy backoff + outcome classification (9 cases)
//   B. State machine (7 cases)
//   C. Concurrency cap (3 cases)
//   D. Store mechanics (7 cases)
//   E. Persistence + hardening (4 cases)
//
// All cases use isolated IndexedDB instances (unique DB names per case) and
// explicit timestamps so the suite is deterministic.

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import type { AttachmentRedownloadRecord } from "./AttachmentRedownload";
import {
  BACKOFF_SCHEDULE,
  MAX_ATTEMPTS,
  CONCURRENT_DOWNLOAD_CAP,
  DISTANT_FUTURE,
  nextEligibleAt,
  classifyOutcome,
  canStartNew,
} from "./AttachmentRedownloadPolicy";
import { transition, startAttempt } from "./AttachmentRedownloadStateMachine";
import { AttachmentRedownloadStore } from "./AttachmentRedownloadStore";

SelfTestRunner.register({
  name: "Attachment re-download (backoff + expiry)",
  isVerified: true,
  run: async () => {
    const cases: TestCase[] = [];

    // -----------------------------------------------------------------------
    // Group A — Policy backoff + outcome classification
    // -----------------------------------------------------------------------

    // A1: backoff schedule shape
    cases.push((() => {
      const s = BACKOFF_SCHEDULE;
      const ok =
        s.length === 8 &&
        s[0] === 1 &&
        s[1] === 4 &&
        s[2] === 16 &&
        s[3] === 60 &&
        s[4] === 300 &&
        s[5] === 300 &&
        s[6] === 300 &&
        s[7] === 300;
      return ok
        ? pass("backoffSchedule has 8 entries matching spec [1,4,16,60,300,300,300,300]")
        : fail("backoffSchedule has 8 entries matching spec [1,4,16,60,300,300,300,300]", `got ${JSON.stringify(s)}`);
    })());

    // A2: nextEligibleAt at attempt 0 → t0 + 1s
    cases.push((() => {
      const t0 = 1_000_000_000;
      const got = nextEligibleAt(0, t0);
      const want = t0 + 1 * 1_000;
      const ok = got === want;
      return ok
        ? pass("nextEligibleAt at attempt 0 → t0 + 1s")
        : fail("nextEligibleAt at attempt 0 → t0 + 1s", `got ${got}, want ${want}`);
    })());

    // A3: nextEligibleAt at attempt 4 → t0 + 300s
    cases.push((() => {
      const t0 = 1_000_000_000;
      const got = nextEligibleAt(4, t0);
      const want = t0 + 300 * 1_000;
      const ok = got === want;
      return ok
        ? pass("nextEligibleAt at attempt 4 → t0 + 300s")
        : fail("nextEligibleAt at attempt 4 → t0 + 300s", `got ${got}, want ${want}`);
    })());

    // A4: nextEligibleAt at attemptCount >= maxAttempts → DISTANT_FUTURE
    cases.push((() => {
      const t0 = 1_000_000_000;
      const got = nextEligibleAt(MAX_ATTEMPTS, t0);
      const ok = got === DISTANT_FUTURE;
      return ok
        ? pass("nextEligibleAt at attemptCount >= maxAttempts → DISTANT_FUTURE")
        : fail("nextEligibleAt at attemptCount >= maxAttempts → DISTANT_FUTURE", `got ${got}`);
    })());

    // A5: classifyOutcome HTTP 200 → success
    cases.push((() => {
      const cls = classifyOutcome({ kind: "permanentFailure", httpStatus: 200 });
      const ok = cls === "success";
      return ok
        ? pass("classifyOutcome HTTP 200 → success")
        : fail("classifyOutcome HTTP 200 → success", `got ${cls}`);
    })());

    // A6: classifyOutcome HTTP 404 → retryable
    cases.push((() => {
      const cls = classifyOutcome({ kind: "permanentFailure", httpStatus: 404 });
      const ok = cls === "retryable";
      return ok
        ? pass("classifyOutcome HTTP 404 → retryable")
        : fail("classifyOutcome HTTP 404 → retryable", `got ${cls}`);
    })());

    // A7: classifyOutcome HTTP 410 → permanent
    cases.push((() => {
      const cls = classifyOutcome({ kind: "permanentFailure", httpStatus: 410 });
      const ok = cls === "permanent";
      return ok
        ? pass("classifyOutcome HTTP 410 → permanent")
        : fail("classifyOutcome HTTP 410 → permanent", `got ${cls}`);
    })());

    // A8: classifyOutcome HTTP 500 → retryable
    cases.push((() => {
      const cls = classifyOutcome({ kind: "permanentFailure", httpStatus: 500 });
      const ok = cls === "retryable";
      return ok
        ? pass("classifyOutcome HTTP 500 → retryable")
        : fail("classifyOutcome HTTP 500 → retryable", `got ${cls}`);
    })());

    // A9: classifyOutcome transientFailure → retryable
    cases.push((() => {
      const cls = classifyOutcome({ kind: "transientFailure", reason: "timeout" });
      const ok = cls === "retryable";
      return ok
        ? pass("classifyOutcome transientFailure → retryable")
        : fail("classifyOutcome transientFailure → retryable", `got ${cls}`);
    })());

    // -----------------------------------------------------------------------
    // Group B — State machine
    // -----------------------------------------------------------------------

    // B1: inFlight + success → completed
    cases.push((() => {
      const now = 2_000_000_000;
      const r = makeInFlight({ attempts: 1 });
      const next = transition(r, { kind: "success", bytes: new Uint8Array([1]) }, now);
      const ok = next.state === "completed";
      return ok
        ? pass("inFlight + success → completed")
        : fail("inFlight + success → completed", `got state=${next.state}`);
    })());

    // B2: inFlight + permanentFailure(410) → expired even at attempt 1
    cases.push((() => {
      const now = 2_000_000_000;
      const r = makeInFlight({ attempts: 1 });
      const next = transition(r, { kind: "permanentFailure", httpStatus: 410 }, now);
      const ok = next.state === "expired" && next.failureHistory[next.failureHistory.length - 1] === 410;
      return ok
        ? pass("inFlight + permanentFailure(410) → expired even at attempt 1")
        : fail("inFlight + permanentFailure(410) → expired even at attempt 1", `state=${next.state} history=${JSON.stringify(next.failureHistory)}`);
    })());

    // B3: inFlight + permanentFailure(404) at attempt 7 → expired (cap reached)
    cases.push((() => {
      const now = 2_000_000_000;
      const r = makeInFlight({ attempts: 7 }); // next count = 8 = MAX_ATTEMPTS
      const next = transition(r, { kind: "permanentFailure", httpStatus: 404 }, now);
      const ok =
        next.state === "expired" &&
        next.attemptCount === 8 &&
        next.failureHistory[next.failureHistory.length - 1] === 404;
      return ok
        ? pass("inFlight + permanentFailure(404) at attempt 7 of 8 → expired (cap reached)")
        : fail("inFlight + permanentFailure(404) at attempt 7 of 8 → expired (cap reached)", `state=${next.state} count=${next.attemptCount} hist=${JSON.stringify(next.failureHistory)}`);
    })());

    // B4: inFlight + permanentFailure(404) at attempt 1 → backoff (counter→2)
    cases.push((() => {
      const now = 2_000_000_000;
      const r = makeInFlight({ attempts: 1 });
      const next = transition(r, { kind: "permanentFailure", httpStatus: 404 }, now);
      const expectedNext = now + 16 * 1_000; // backoffSchedule[2] = 16s for attemptCount 2
      const ok =
        next.state === "backoff" &&
        next.attemptCount === 2 &&
        next.nextEligibleAt === expectedNext &&
        JSON.stringify(next.failureHistory) === JSON.stringify([404]);
      return ok
        ? pass("inFlight + permanentFailure(404) at attempt 1 → backoff (counter→2)")
        : fail("inFlight + permanentFailure(404) at attempt 1 → backoff (counter→2)", `state=${next.state} count=${next.attemptCount} next=${next.nextEligibleAt} hist=${JSON.stringify(next.failureHistory)}`);
    })());

    // B5: inFlight + transientFailure → backoff, count NOT incremented
    cases.push((() => {
      const now = 2_000_000_000;
      const r = makeInFlight({ attempts: 3 });
      const next = transition(r, { kind: "transientFailure", reason: "dns" }, now);
      const ok =
        next.state === "backoff" &&
        next.attemptCount === 3 && // unchanged
        next.nextEligibleAt === now + 4_000 &&
        JSON.stringify(next.failureHistory) === JSON.stringify(r.failureHistory); // unchanged
      return ok
        ? pass("inFlight + transientFailure → backoff, count NOT incremented")
        : fail("inFlight + transientFailure → backoff, count NOT incremented", `state=${next.state} count=${next.attemptCount} next=${next.nextEligibleAt} hist=${JSON.stringify(next.failureHistory)}`);
    })());

    // B6: startAttempt sets inFlight and stamps lastAttemptedAt
    cases.push((() => {
      const now = 2_000_000_000;
      const r = makePending();
      const next = startAttempt(r, now);
      const ok = next.state === "inFlight" && next.lastAttemptedAt === now;
      return ok
        ? pass("startAttempt sets inFlight and stamps lastAttemptedAt")
        : fail("startAttempt sets inFlight and stamps lastAttemptedAt", `state=${next.state} last=${next.lastAttemptedAt}`);
    })());

    // B7: failureHistory grows only on permanent failures
    cases.push((() => {
      const now = 2_000_000_000;
      let r = makeInFlight({ attempts: 0 });
      // Two transients should NOT append to history
      r = transition(r, { kind: "transientFailure", reason: "x" }, now);
      r = transition(startAttempt(r, now), { kind: "transientFailure", reason: "y" }, now);
      // One 500 should append
      r = transition(startAttempt(r, now), { kind: "permanentFailure", httpStatus: 500 }, now);
      const ok =
        JSON.stringify(r.failureHistory) === JSON.stringify([500]) &&
        r.attemptCount === 1;
      return ok
        ? pass("failureHistory grows only on permanent failures")
        : fail("failureHistory grows only on permanent failures", `history=${JSON.stringify(r.failureHistory)} count=${r.attemptCount}`);
    })());

    // -----------------------------------------------------------------------
    // Group C — Concurrency cap
    // -----------------------------------------------------------------------

    // C1: canStartNew at inFlight=3 → true
    cases.push((() => {
      const ok = canStartNew(3) === true;
      return ok
        ? pass(`canStartNew at inFlight=3 (cap ${CONCURRENT_DOWNLOAD_CAP}) → true`)
        : fail(`canStartNew at inFlight=3 (cap ${CONCURRENT_DOWNLOAD_CAP}) → true`, "returned false");
    })());

    // C2: canStartNew at inFlight=4 → false (at cap)
    cases.push((() => {
      const ok = canStartNew(4) === false;
      return ok
        ? pass("canStartNew at inFlight=4 → false (at cap)")
        : fail("canStartNew at inFlight=4 → false (at cap)", "returned true");
    })());

    // C3: canStartNew at inFlight=5 → false (over cap)
    cases.push((() => {
      const ok = canStartNew(5) === false;
      return ok
        ? pass("canStartNew at inFlight=5 → false (over cap)")
        : fail("canStartNew at inFlight=5 → false (over cap)", "returned true");
    })());

    // -----------------------------------------------------------------------
    // Group D — Store mechanics
    // -----------------------------------------------------------------------

    // D1: register creates a pending record
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 100;
      const r = await s.register("att-1", t0);
      const persisted = await s.record("att-1");
      const ok = r.state === "pending" && r.attemptCount === 0 && persisted?.state === "pending";
      return ok
        ? pass("register creates a pending record")
        : fail("register creates a pending record", `register=${JSON.stringify(r)} persisted=${JSON.stringify(persisted)}`);
    })());

    // D2: register is idempotent (does not reset in-progress record)
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 100;
      const t1 = 999;
      await s.register("att-1", t0);
      await s.markInFlight("att-1", t0);
      await s.applyOutcome("att-1", { kind: "permanentFailure", httpStatus: 404 }, t0);
      const second = await s.register("att-1", t1);
      const ok = second.state === "backoff" && second.attemptCount === 1;
      return ok
        ? pass("register is idempotent (does not reset in-progress record)")
        : fail("register is idempotent (does not reset in-progress record)", `second=${JSON.stringify(second)}`);
    })());

    // D3: markInFlight transitions to inFlight and persists
    cases.push(await (async () => {
      const s = freshStore();
      await s.register("att-1", 100);
      const now = 200;
      const r = await s.markInFlight("att-1", now);
      const persisted = await s.record("att-1");
      const ok =
        r?.state === "inFlight" &&
        r?.lastAttemptedAt === now &&
        persisted?.state === "inFlight";
      return ok
        ? pass("markInFlight transitions to inFlight and persists")
        : fail("markInFlight transitions to inFlight and persists", `r=${JSON.stringify(r)} persisted=${JSON.stringify(persisted)}`);
    })());

    // D4: applyOutcome runs the transition and persists
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 100;
      await s.register("att-1", t0);
      await s.markInFlight("att-1", t0);
      const next = await s.applyOutcome("att-1", { kind: "permanentFailure", httpStatus: 410 }, t0);
      const persisted = await s.record("att-1");
      const ok =
        next?.state === "expired" &&
        persisted?.state === "expired" &&
        JSON.stringify(persisted?.failureHistory) === JSON.stringify([410]);
      return ok
        ? pass("applyOutcome runs the transition and persists")
        : fail("applyOutcome runs the transition and persists", `next=${JSON.stringify(next)} persisted=${JSON.stringify(persisted)}`);
    })());

    // D5: dueForRetry filters backoff with nextEligibleAt<=now, sorted ascending
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 1_000;
      for (const id of ["A", "B", "C", "D", "E"]) {
        await s.register(id, t0);
        await s.markInFlight(id, t0);
      }
      // A → backoff, 1 counted failure → nextEligibleAt = t0 + backoffSchedule[1]*1000 = t0+4000ms
      await s.applyOutcome("A", { kind: "permanentFailure", httpStatus: 404 }, t0);
      // B → 2 counted failures → nextEligibleAt = t0 + backoffSchedule[2]*1000 = t0+16000ms
      await s.applyOutcome("B", { kind: "permanentFailure", httpStatus: 404 }, t0);
      await s.markInFlight("B", t0);
      await s.applyOutcome("B", { kind: "permanentFailure", httpStatus: 404 }, t0);
      // C → transient → fixed 4s reschedule = t0+4000ms
      await s.applyOutcome("C", { kind: "transientFailure", reason: "x" }, t0);
      // D stays inFlight (never gets an outcome)
      // E → expired via 410
      await s.applyOutcome("E", { kind: "permanentFailure", httpStatus: 410 }, t0);

      // Query at t0 + 10_000_000 ms so all are past-due
      const now = t0 + 10_000_000;
      const due = await s.dueForRetry(now);
      const dueIds = due.map((r) => r.attachmentId);

      // Must include A, B, C; must NOT include D (inFlight) or E (expired)
      // Must be sorted ascending by nextEligibleAt → B has the largest so must be last
      const ok =
        dueIds.includes("A") &&
        dueIds.includes("B") &&
        dueIds.includes("C") &&
        !dueIds.includes("D") &&
        !dueIds.includes("E") &&
        dueIds[dueIds.length - 1] === "B";
      return ok
        ? pass("dueForRetry filters backoff with nextEligibleAt<=now, sorted ascending")
        : fail("dueForRetry filters backoff with nextEligibleAt<=now, sorted ascending", `due=${JSON.stringify(dueIds)}`);
    })());

    // D6: expiredAttachmentIds set reflects expired records
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 1_000;
      for (const id of ["X", "Y", "Z"]) {
        await s.register(id, t0);
        await s.markInFlight(id, t0);
      }
      await s.applyOutcome("X", { kind: "permanentFailure", httpStatus: 410 }, t0);
      // Y stays inFlight
      // Z burns the cap by exhausting all 8 attempts
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        await s.markInFlight("Z", t0);
        await s.applyOutcome("Z", { kind: "permanentFailure", httpStatus: 404 }, t0);
      }
      const expired = await s.expiredAttachmentIds();
      const rZ = await s.record("Z");
      const ok =
        expired.has("X") &&
        expired.has("Z") &&
        !expired.has("Y") &&
        rZ?.state === "expired";
      return ok
        ? pass("expiredAttachmentIds set reflects expired records")
        : fail("expiredAttachmentIds set reflects expired records", `expired=${JSON.stringify([...expired])} rZ.state=${rZ?.state}`);
    })());

    // D7: inFlightCount counts only inFlight records
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 1_000;
      for (const id of ["A", "B", "C"]) {
        await s.register(id, t0);
      }
      await s.markInFlight("A", t0);
      await s.markInFlight("B", t0);
      // C stays pending
      const count = await s.inFlightCount();
      const ok = count === 2;
      return ok
        ? pass("inFlightCount counts only inFlight records")
        : fail("inFlightCount counts only inFlight records", `got ${count}`);
    })());

    // -----------------------------------------------------------------------
    // Group E — Persistence + hardening
    // -----------------------------------------------------------------------

    // E1: pending record survives store recreation (same DB name)
    cases.push(await (async () => {
      const dbName = uniqueDbName();
      const t0 = 100;
      const writer = AttachmentRedownloadStore.forTesting(dbName);
      await writer.register("att-1", t0);
      // Open a second store instance pointing at the same DB
      const reader = AttachmentRedownloadStore.forTesting(dbName);
      const recovered = await reader.record("att-1");
      const ok = recovered?.state === "pending" && recovered?.attachmentId === "att-1";
      return ok
        ? pass("pending record survives store recreation")
        : fail("pending record survives store recreation", `recovered=${JSON.stringify(recovered)}`);
    })());

    // E2: forget removes one record only
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 100;
      await s.register("A", t0);
      await s.register("B", t0);
      await s.forget("A");
      const a = await s.record("A");
      const b = await s.record("B");
      const ok = a === undefined && b !== undefined;
      return ok
        ? pass("forget removes one record only")
        : fail("forget removes one record only", `A=${JSON.stringify(a)} B=${JSON.stringify(b)}`);
    })());

    // E3: resetAll empties the store
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 100;
      await s.register("A", t0);
      await s.register("B", t0);
      await s.resetAll();
      const a = await s.record("A");
      const b = await s.record("B");
      const ok = a === undefined && b === undefined;
      return ok
        ? pass("resetAll empties the store")
        : fail("resetAll empties the store", "A or B still present");
    })());

    // E4: expired is monotonic against subsequent outcomes
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 100;
      await s.register("att-1", t0);
      await s.markInFlight("att-1", t0);
      const expired = await s.applyOutcome("att-1", { kind: "permanentFailure", httpStatus: 410 }, t0);
      if (expired?.state !== "expired") {
        return fail("expired is monotonic against subsequent outcomes", `didn't expire on 410: ${JSON.stringify(expired)}`);
      }
      // A later 404 MUST NOT undo the expired verdict
      const again = await s.applyOutcome("att-1", { kind: "permanentFailure", httpStatus: 404 }, t0 + 100);
      const ok =
        again?.state === "expired" &&
        again?.attemptCount === expired?.attemptCount &&
        JSON.stringify(again?.failureHistory) === JSON.stringify(expired?.failureHistory);
      return ok
        ? pass("expired is monotonic against subsequent outcomes")
        : fail("expired is monotonic against subsequent outcomes", `again=${JSON.stringify(again)}`);
    })());

    // -----------------------------------------------------------------------
    // Bonus: per-attachment isolation (separate records don't interfere)
    // -----------------------------------------------------------------------
    cases.push(await (async () => {
      const s = freshStore();
      const t0 = 1_000;
      await s.register("alpha", t0);
      await s.register("beta", t0);
      await s.markInFlight("alpha", t0);
      await s.applyOutcome("alpha", { kind: "permanentFailure", httpStatus: 410 }, t0);
      const alpha = await s.record("alpha");
      const beta = await s.record("beta");
      const ok = alpha?.state === "expired" && beta?.state === "pending";
      return ok
        ? pass("per-attachment isolation: transitions on one record do not affect another")
        : fail("per-attachment isolation: transitions on one record do not affect another", `alpha=${alpha?.state} beta=${beta?.state}`);
    })());

    return buildSuiteResult(cases);
  },
});

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

let _dbCounter = 0;

function uniqueDbName(): string {
  return `kaether_ar_test_${Date.now()}_${++_dbCounter}`;
}

function freshStore(): AttachmentRedownloadStore {
  return AttachmentRedownloadStore.forTesting(uniqueDbName());
}

function makePending(): AttachmentRedownloadRecord {
  const t0 = 1_000_000_000;
  return {
    attachmentId: "att-1",
    state: "pending",
    attemptCount: 0,
    lastAttemptedAt: t0,
    nextEligibleAt: t0,
    failureHistory: [],
  };
}

function makeInFlight({ attempts }: { attempts: number }): AttachmentRedownloadRecord {
  const t0 = 1_000_000_000;
  return {
    attachmentId: "att-1",
    state: "inFlight",
    attemptCount: attempts,
    lastAttemptedAt: t0,
    nextEligibleAt: t0,
    failureHistory: [],
  };
}
